/**
 * Diet — the v2 nutrition screen.
 *
 * Implements the Diet screen of AppScreen.dc.html ("OnFlow Design System"
 * project) wired to live data:
 *
 *   header status  → today's intake vs the active NutritionTarget band
 *   range segment  → Today | Week (the API exposes one IST week, not a month)
 *   totals card    → /logs/nutrition/daily-totals vs the target band midpoint
 *   meal cards     → today's NutritionLog entries grouped by mealType, with
 *                    the active diet plan's per-slot kcal budget as the target
 *   add sheet      → FlowSheet over the food catalog; the ADR-0015 unit-aware
 *                    controls (count / portion / grams / manual override) are
 *                    unchanged, they now live inside the sheet
 *   toast          → confirms the write and offers a one-tap undo (DELETE)
 *
 * Deviations from the template: the range segment is Today | Week (no Month
 * endpoint exists), and the strip shows the review's 7 IST days rather than
 * the template's 14.
 */
import type {
  Food,
  FoodCategory,
  FoodMeasure,
  FoodSizeLabel,
  FoodSizes,
  MealType,
  NutritionLog,
} from '@onflow/shared';
import { useDeferredValue, useEffect, useMemo, useState } from 'react';

import { SkeletonBlock } from '../components/SkeletonBlock.js';
import { ConsistencyStrip } from '../components/v2/ConsistencyStrip.js';
import { FlowButton } from '../components/v2/FlowButton.js';
import { FlowInput, FlowSegment } from '../components/v2/FlowInput.js';
import { FlowSheet } from '../components/v2/FlowSheet.js';
import { FlowStatus, type TrackState } from '../components/v2/FlowStatus.js';
import { Icon } from '../components/v2/Icon.js';
import { MacroGroup, MacroRow } from '../components/v2/MacroRow.js';
import {
  useFavoriteFoods,
  useFoodsQuery,
  useRecentFoods,
  useToggleFavoriteFood,
} from '../hooks/useFoods.js';
import {
  useClearDayMutation,
  useCreateNutritionLog,
  useDeleteNutritionLog,
  useNutritionLogsQuery,
  usePatchNutritionLog,
} from '../hooks/useNutritionLogs.js';
import { useCurrentReviewQuery } from '../hooks/useReview.js';
import { useNutritionTarget } from '../hooks/useTargets.js';
import { displayUnit, fmtQuantity } from '../lib/food-units.js';
import { formatNutritionLog } from '../lib/format-food.js';
import { toast } from '../lib/toast.js';
import { consistencyDays, consistencySentence, localDate } from '../lib/track.js';

const QTY_MIN = 10;
const QTY_MAX = 500;
const QTY_STEP = 5;
const MANUAL_QTY_MAX = 5000; // matches NutritionLogsService QUANTITY_CEILING_G
const COUNT_MIN = 1;
const COUNT_MAX = 20;

const SIZE_LABELS: ReadonlyArray<FoodSizeLabel> = ['small', 'medium', 'large'];

const MEAL_TYPES: ReadonlyArray<MealType> = ['breakfast', 'lunch', 'snacks', 'dinner'];
const MEAL_LABEL: Record<MealType, string> = {
  breakfast: 'Breakfast',
  lunch: 'Lunch',
  dinner: 'Dinner',
  snacks: 'Snacks',
};

const CATEGORY_TABS: ReadonlyArray<{ key: FoodCategory | null; label: string }> = [
  { key: null, label: 'All' },
  { key: 'protein', label: 'Protein' },
  { key: 'grain', label: 'Grain' },
  { key: 'vegetable', label: 'Vegetable' },
  { key: 'fruit', label: 'Fruit' },
  { key: 'dairy', label: 'Dairy' },
  { key: 'legume', label: 'Legume' },
  { key: 'snack', label: 'Snack' },
  { key: 'restaurant', label: 'Restaurant' },
];

const EYEBROW = {
  fontFamily: 'var(--font-mono)',
  fontSize: 10,
  letterSpacing: '0.12em',
  textTransform: 'uppercase' as const,
  color: 'var(--ink-3)',
};

const CARD = {
  background: 'var(--paper-1)',
  border: '1px solid var(--edge)',
  borderRadius: 'var(--r-md)',
  boxShadow: 'var(--elev-1)',
};

/** Which surface the sheet is showing. `closed` keeps the sheet unmounted. */
type SheetMode =
  | { kind: 'closed' }
  | { kind: 'browse'; meal: MealType }
  | { kind: 'new'; meal: MealType; food: Food }
  | { kind: 'edit'; entry: NutritionLog };

// ADR-0015 input modes. The sheet renders different controls per mode and the
// submit payload routes accordingly. `manual` is the override path — the
// food's unitType is not grams but the user wants to log a precise weight.
type InputMode = 'count' | 'portion' | 'grams' | 'manual';

interface PendingToast {
  logId: string;
  title: string;
  meta: string;
}

function localTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

function todayLocalDate(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: localTimezone(),
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

function defaultMealForNow(): MealType {
  const h = new Date().getHours();
  if (h < 11) return 'breakfast';
  if (h < 15) return 'lunch';
  if (h < 17) return 'snacks';
  return 'dinner';
}

function clampQty(q: number, max = QTY_MAX): number {
  if (!Number.isFinite(q)) return QTY_MIN;
  if (q < QTY_MIN) return QTY_MIN;
  if (q > max) return max;
  return Math.round(q / QTY_STEP) * QTY_STEP;
}

function clampCount(n: number): number {
  if (!Number.isFinite(n)) return COUNT_MIN;
  if (n < COUNT_MIN) return COUNT_MIN;
  if (n > COUNT_MAX) return COUNT_MAX;
  return Math.round(n);
}

function round1(n: number): number {
  return Math.round(n * 10) / 10;
}

function modeForFood(food: Food): InputMode {
  if (food.unitType === 'count') return 'count';
  if (food.unitType === 'portion') return 'portion';
  return 'grams';
}

function modeForExisting(entry: NutritionLog, food: Food | undefined): InputMode {
  if (entry.count != null) return 'count';
  if (entry.portionLabel != null) return 'portion';
  // Bare quantityG entry. If we know the food and it is grams-mode, show
  // grams; otherwise the entry came from the manual override path, so open it
  // in manual mode and editing will not 400 on save.
  if (food && food.unitType !== 'grams') return 'manual';
  return 'grams';
}

/** Deterministic meal guidance derived from the daily target, never a meal plan. */
function slotBudgets(dailyKcal: number): Record<MealType, number> {
  return {
    breakfast: Math.round(dailyKcal * 0.25),
    lunch: Math.round(dailyKcal * 0.3),
    snacks: Math.round(dailyKcal * 0.15),
    dinner: Math.round(dailyKcal * 0.3),
  };
}

export function Log() {
  const tz = localTimezone();
  const today = todayLocalDate();

  const [sheet, setSheet] = useState<SheetMode>({ kind: 'closed' });
  const [range, setRange] = useState<'today' | 'week'>('today');
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<FoodCategory | null>(null);
  const [quantityG, setQuantityG] = useState(100);
  const [count, setCount] = useState(1);
  const [sizeLabel, setSizeLabel] = useState<FoodSizeLabel | null>(null);
  const [portionLabel, setPortionLabel] = useState<FoodSizeLabel>('medium');
  const [inputMode, setInputMode] = useState<InputMode>('grams');
  // Named household measures (handful, tablespoon, small bowl …) for grams-mode
  // foods. These are a grams-input convenience only: selecting one sets
  // quantityG = measure.grams × measureCount, so the log still travels the
  // existing grams path. measureLabel === null means the user is on free grams
  // (dragged the slider directly). Not persisted on the log.
  const [measureLabel, setMeasureLabel] = useState<string | null>(null);
  const [measureCount, setMeasureCount] = useState(1);
  const [mealType, setMealType] = useState<MealType>(defaultMealForNow());
  const [pendingToast, setPendingToast] = useState<PendingToast | null>(null);

  const todayLogs = useNutritionLogsQuery({ date: today, timezone: tz });
  const deferredSearch = useDeferredValue(search.trim());
  const foods = useFoodsQuery({
    ...(category ? { category } : {}),
    ...(deferredSearch ? { search: deferredSearch } : {}),
  });
  const loggedFoodIds = useMemo(
    () => [...new Set((todayLogs.data?.entries ?? []).map((entry) => entry.foodId))],
    [todayLogs.data?.entries],
  );
  const loggedFoods = useFoodsQuery({ ids: loggedFoodIds });
  const recentFoods = useRecentFoods();
  const favoriteFoods = useFavoriteFoods();
  const toggleFavorite = useToggleFavoriteFood();
  const targetQuery = useNutritionTarget();
  const review = useCurrentReviewQuery();

  const createMut = useCreateNutritionLog();
  const patchMut = usePatchNutritionLog();
  const deleteMut = useDeleteNutritionLog();
  const clearDayMut = useClearDayMutation();

  const foodMap = useMemo(() => {
    const map = new Map<string, Food>();
    for (const f of loggedFoods.data?.items ?? []) map.set(f.id, f);
    for (const f of foods.data?.items ?? []) map.set(f.id, f);
    return map;
  }, [loggedFoods.data?.items, foods.data?.items]);

  const filteredFoods = foods.data?.items ?? [];

  const favoriteIds = useMemo(
    () => new Set((favoriteFoods.data?.items ?? []).map((item) => item.foodId)),
    [favoriteFoods.data?.items],
  );

  // Zero-result telemetry — console.warn so catalog expansion can mine browser
  // logs until a proper telemetry endpoint exists.
  useEffect(() => {
    const q = search.trim();
    if (q === deferredSearch && q.length >= 2 && !foods.isFetching && filteredFoods.length === 0) {
      console.warn('food_search_zero_results', { query: q });
    }
  }, [search, deferredSearch, filteredFoods.length, foods.isFetching]);

  // Undo window. Long enough to catch a mis-tap, short enough that the toast
  // never covers the nav for a whole session.
  useEffect(() => {
    if (!pendingToast) return;
    const t = window.setTimeout(() => setPendingToast(null), 6000);
    return () => window.clearTimeout(t);
  }, [pendingToast]);

  const editingEntry = sheet.kind === 'edit' ? sheet.entry : null;
  const activeFood: Food | undefined =
    sheet.kind === 'new' ? sheet.food : editingEntry ? foodMap.get(editingEntry.foodId) : undefined;
  const activeSizes: FoodSizes | null = (activeFood?.sizes as FoodSizes | null) ?? null;
  // Named measures only surface for grams-mode foods that carry them; count and
  // portion foods already have their own natural-unit controls.
  const activeMeasures: FoodMeasure[] | null =
    activeFood && activeFood.unitType === 'grams' ? (activeFood.measures ?? null) : null;
  const measureUnit = displayUnit(activeFood);

  function openBrowse(meal: MealType): void {
    setSearch('');
    setMealType(meal);
    setSheet({ kind: 'browse', meal });
  }

  function startEditNew(food: Food): void {
    const meal = sheet.kind === 'browse' ? sheet.meal : defaultMealForNow();
    setInputMode(modeForFood(food));
    setCount(1);
    setSizeLabel(food.sizes ? 'medium' : null);
    setPortionLabel('medium');
    setMealType(meal);
    // For a grams-mode food with named measures, open on the first measure
    // (e.g. "1 handful = 28g") so the user sees a unit, not a bare gram slider.
    const measures = food.unitType === 'grams' ? (food.measures ?? null) : null;
    const firstMeasure = measures && measures.length > 0 ? measures[0] : null;
    setMeasureCount(1);
    if (firstMeasure) {
      setMeasureLabel(firstMeasure.label);
      setQuantityG(measureGrams(firstMeasure.grams, 1));
    } else {
      setMeasureLabel(null);
      setQuantityG(clampQty(food.defaultServingG));
    }
    setSheet({ kind: 'new', meal, food });
  }

  function startEditExisting(entry: NutritionLog): void {
    const food = foodMap.get(entry.foodId);
    setInputMode(modeForExisting(entry, food));
    setCount(entry.count ?? 1);
    setSizeLabel((entry.sizeLabel as FoodSizeLabel | null) ?? null);
    setPortionLabel((entry.portionLabel as FoodSizeLabel | null) ?? 'medium');
    setQuantityG(clampQty(entry.quantityG, MANUAL_QTY_MAX));
    // A persisted log carries no measure label — edit on free grams.
    setMeasureLabel(null);
    setMeasureCount(1);
    setMealType(entry.mealType);
    setSheet({ kind: 'edit', entry });
  }

  function closeSheet(): void {
    setSheet({ kind: 'closed' });
  }

  // Exact grams for a measure selection — NOT clampQty'd (that rounds to the
  // slider's 5g step and 10g floor, which would crush a 1.2g almond piece).
  function measureGrams(gramsPerUnit: number, n: number): number {
    return Math.round(gramsPerUnit * n * 10) / 10;
  }

  function applyMeasure(m: FoodMeasure): void {
    setMeasureLabel(m.label);
    setMeasureCount(1);
    setQuantityG(measureGrams(m.grams, 1));
  }

  function stepMeasureCount(m: FoodMeasure, delta: number): void {
    const n = clampCount(measureCount + delta);
    setMeasureCount(n);
    setQuantityG(measureGrams(m.grams, n));
  }

  // Leave the measure UI for the free-grams slider (custom weight).
  function useCustomGrams(): void {
    setMeasureLabel(null);
    setQuantityG(clampQty(quantityG, inputMode === 'manual' ? MANUAL_QTY_MAX : QTY_MAX));
  }

  function sheetBack(): void {
    if (sheet.kind === 'new') openBrowse(sheet.meal);
    else closeSheet();
  }

  // Resolve canonical grams for the live preview. The server is still the
  // authority on the persisted quantityG; this is UX feedback only.
  function previewGrams(): number {
    if (!activeFood) return quantityG;
    if (inputMode === 'count') {
      const sizes = activeFood.sizes as FoodSizes | null;
      const perUnit = sizeLabel && sizes ? sizes[sizeLabel] : (activeFood.defaultGramsPerUnit ?? 0);
      return count * perUnit;
    }
    if (inputMode === 'portion') {
      const sizes = activeFood.sizes as FoodSizes | null;
      return sizes ? sizes[portionLabel] : 0;
    }
    return quantityG; // grams or manual
  }

  async function save(): Promise<void> {
    if (sheet.kind === 'new') {
      const food = sheet.food;
      try {
        const base = {
          foodId: food.id,
          mealType,
          timezone: tz,
          loggedAt: new Date().toISOString(),
          source: 'manual' as const,
        };
        const created =
          inputMode === 'count'
            ? await createMut.mutateAsync({
                ...base,
                count,
                ...(food.sizes && sizeLabel ? { sizeLabel } : {}),
              })
            : inputMode === 'portion'
              ? await createMut.mutateAsync({ ...base, portionLabel })
              : inputMode === 'manual'
                ? await createMut.mutateAsync({ ...base, quantityG, manualOverride: true })
                : await createMut.mutateAsync({ ...base, quantityG });
        setPendingToast({
          logId: created.id,
          title: `${food.name} → ${MEAL_LABEL[mealType]}`,
          meta: `+${Math.round(created.kcal)} kcal · +${round1(created.proteinG)} g protein`,
        });
        closeSheet();
        setSearch('');
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Could not log entry');
      }
      return;
    }
    if (sheet.kind === 'edit') {
      const id = sheet.entry.id;
      try {
        if (inputMode === 'count') {
          await patchMut.mutateAsync({
            id,
            patch: { count, ...(sizeLabel ? { sizeLabel } : { sizeLabel: null }), mealType },
          });
        } else if (inputMode === 'portion') {
          await patchMut.mutateAsync({ id, patch: { portionLabel, mealType } });
        } else if (inputMode === 'manual') {
          await patchMut.mutateAsync({ id, patch: { quantityG, manualOverride: true, mealType } });
        } else {
          await patchMut.mutateAsync({ id, patch: { quantityG, mealType } });
        }
        toast.success('Updated');
        closeSheet();
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Could not update entry');
      }
    }
  }

  async function undoLastAdd(): Promise<void> {
    const t = pendingToast;
    if (!t) return;
    setPendingToast(null);
    try {
      await deleteMut.mutateAsync(t.logId);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not undo');
    }
  }

  async function deleteEntry(entry: NutritionLog): Promise<void> {
    const ok = window.confirm(`Delete this entry from today's log?`);
    if (!ok) return;
    try {
      await deleteMut.mutateAsync(entry.id);
      toast.success('Deleted');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not delete entry');
    }
  }

  async function clearAllToday(): Promise<void> {
    const n = todayLogs.data?.entries.length ?? 0;
    if (n === 0) return;
    const ok = window.confirm(
      `Delete all ${n} entr${n === 1 ? 'y' : 'ies'} logged today? This cannot be undone.`,
    );
    if (!ok) return;
    try {
      const res = await clearDayMut.mutateAsync({ timezone: tz });
      toast.success(`Deleted ${res.deletedCount} entr${res.deletedCount === 1 ? 'y' : 'ies'}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not clear today');
    }
  }

  // ── Derived numbers ─────────────────────────────────────────────────────
  const grams = previewGrams();
  const preview = activeFood
    ? {
        kcal: Math.round((activeFood.kcalPer100g * grams) / 100),
        proteinG: round1((activeFood.proteinPer100g * grams) / 100),
        carbsG: round1((activeFood.carbsPer100g * grams) / 100),
        fatG: round1((activeFood.fatPer100g * grams) / 100),
      }
    : null;

  const entries = todayLogs.data?.entries ?? [];
  const byMeal = todayLogs.data?.totals.byMealType;
  const total = todayLogs.data?.totals.total ?? {
    kcal: 0,
    proteinG: 0,
    carbsG: 0,
    fatG: 0,
    entryCount: 0,
  };
  const target = targetQuery.data?.target ?? null;
  const kcalTarget = target ? Math.round((target.calorieMin + target.calorieMax) / 2) : 0;
  const budgets = slotBudgets(kcalTarget);

  const content = review.data?.review.content;
  const perDay = content?.schemaVersion === 'v2' ? content.adherence.perDay : undefined;
  const days = consistencyDays(perDay, localDate(new Date()));
  const weekLoggedDays = (perDay ?? []).filter((d) => d.actualKcal > 0);
  const weekAvgKcal =
    weekLoggedDays.length > 0
      ? Math.round(weekLoggedDays.reduce((s, d) => s + d.actualKcal, 0) / weekLoggedDays.length)
      : 0;
  const weekAvgProtein =
    weekLoggedDays.length > 0
      ? Math.round(weekLoggedDays.reduce((s, d) => s + d.actualProteinG, 0) / weekLoggedDays.length)
      : 0;
  const weekAvgCarbs =
    weekLoggedDays.length > 0
      ? Math.round(
          weekLoggedDays.reduce((s, d) => s + (d.actualCarbsG ?? 0), 0) / weekLoggedDays.length,
        )
      : 0;
  const weekAvgFat =
    weekLoggedDays.length > 0
      ? Math.round(
          weekLoggedDays.reduce((s, d) => s + (d.actualFatG ?? 0), 0) / weekLoggedDays.length,
        )
      : 0;

  const shownKcal = range === 'today' ? Math.round(total.kcal) : weekAvgKcal;
  const over = kcalTarget > 0 ? shownKcal - kcalTarget : 0;
  const dietState: TrackState =
    kcalTarget === 0 || shownKcal === 0
      ? 'neutral'
      : Math.abs(over) <= kcalTarget * 0.15
        ? 'on-track'
        : over > 0
          ? 'off-track'
          : 'drifting';
  const statusLabel =
    kcalTarget === 0
      ? 'No target'
      : shownKcal === 0
        ? 'Nothing logged'
        : over > 0
          ? `Over by ${over}`
          : `${Math.abs(over)} kcal left`;

  const isPending = createMut.isPending || patchMut.isPending;
  const overrideAvailable = activeFood != null && activeFood.unitType !== 'grams';
  const naturalMode: InputMode | null = activeFood ? modeForFood(activeFood) : null;
  // How much of the slot's plan budget is still unspent — the one number that
  // makes "add to breakfast" a decision rather than a guess.
  const sheetMeal = sheet.kind === 'browse' || sheet.kind === 'new' ? sheet.meal : null;
  const slotLeft =
    sheetMeal == null ? 0 : (budgets[sheetMeal] ?? 0) - Math.round(byMeal?.[sheetMeal]?.kcal ?? 0);

  return (
    <section className="fade-in" data-testid="food-log-drawer" aria-labelledby="diet-title">
      <header
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 12,
          padding: '6px 0 14px',
        }}
      >
        <div style={{ minWidth: 0 }}>
          <span style={{ ...EYEBROW, display: 'block', marginBottom: 6 }}>Nutrition</span>
          <h1
            id="diet-title"
            style={{
              margin: 0,
              fontFamily: 'var(--font-display)',
              fontSize: 'var(--text-title-size)',
              lineHeight: 'var(--text-title-line)',
              letterSpacing: 'var(--text-title-tracking)',
              fontWeight: 700,
              color: 'var(--ink-1)',
            }}
          >
            Diet
          </h1>
        </div>
        <FlowStatus state={dietState} label={statusLabel} />
      </header>

      <FlowSegment
        aria-label="Range"
        items={[
          { id: 'today', label: 'Today' },
          { id: 'week', label: 'Week' },
        ]}
        active={range}
        onSelect={(id) => setRange(id as 'today' | 'week')}
        style={{ marginBottom: 14 }}
      />

      <div
        style={{
          ...CARD,
          border: `1px solid ${
            dietState === 'off-track'
              ? 'color-mix(in srgb, var(--off-track) 40%, transparent)'
              : 'var(--edge)'
          }`,
          padding: 18,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 12,
          }}
        >
          <div>
            <span style={{ ...EYEBROW, fontSize: 11, display: 'block' }}>
              {range === 'today' ? 'Eaten today' : 'Daily average this week'}
            </span>
            <div
              style={{
                marginTop: 8,
                fontFamily: 'var(--font-display)',
                fontSize: 'var(--text-metric-size)',
                lineHeight: 1,
                letterSpacing: 'var(--text-metric-tracking)',
                fontWeight: 700,
                fontVariantNumeric: 'tabular-nums',
                whiteSpace: 'nowrap',
              }}
            >
              {shownKcal.toLocaleString()}
              <span
                style={{
                  color: 'var(--ink-3)',
                  fontWeight: 600,
                  fontSize: '0.42em',
                  marginLeft: 6,
                }}
              >
                {kcalTarget > 0 ? `/ ${kcalTarget.toLocaleString()} kcal` : 'kcal'}
              </span>
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <span style={{ ...EYEBROW, fontSize: 11, display: 'block' }}>
              {over > 0 ? 'Over' : 'Left'}
            </span>
            <div
              style={{
                marginTop: 8,
                fontFamily: 'var(--font-display)',
                fontSize: 'var(--text-metric-sm-size)',
                lineHeight: 1,
                letterSpacing: '-0.03em',
                fontWeight: 700,
                fontVariantNumeric: 'tabular-nums',
                color: over > 0 ? 'var(--off-track)' : 'var(--ink-1)',
              }}
            >
              {kcalTarget === 0 ? '-' : over > 0 ? `+${over}` : Math.abs(over).toLocaleString()}
            </div>
          </div>
        </div>
        <div style={{ height: 1, background: 'var(--edge)', margin: '16px 0' }} />
        <MacroGroup
          protein={{
            current: range === 'today' ? total.proteinG : weekAvgProtein,
            target: target?.proteinMaxG ?? 0,
          }}
          carbs={{
            current: range === 'today' ? total.carbsG : weekAvgCarbs,
            target: target?.carbsMaxG ?? 0,
          }}
          fat={{
            current: range === 'today' ? total.fatG : weekAvgFat,
            target: target?.fatMaxG ?? 0,
          }}
        />
      </div>

      {range === 'today' ? (
        <>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 10,
              margin: '22px 0 10px',
            }}
          >
            <span style={EYEBROW}>Meals</span>
            {entries.length > 0 && (
              <button
                type="button"
                onClick={() => void clearAllToday()}
                disabled={clearDayMut.isPending}
                data-testid="food-log-clear-day"
                style={{
                  ...EYEBROW,
                  background: 'none',
                  border: 0,
                  padding: 0,
                  cursor: 'pointer',
                  color: 'var(--ink-2)',
                }}
              >
                {clearDayMut.isPending ? 'Deleting…' : 'Delete all today'}
              </button>
            )}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {MEAL_TYPES.map((meal) => {
              const mealEntries = entries.filter((e) => e.mealType === meal);
              const kcal = Math.round(byMeal?.[meal]?.kcal ?? 0);
              const budget = budgets[meal];
              const isOver = budget != null && kcal > budget * 1.05;
              const empty = mealEntries.length === 0;
              return (
                <div
                  key={meal}
                  style={{
                    ...CARD,
                    border: `1px solid ${
                      isOver
                        ? 'color-mix(in srgb, var(--off-track) 40%, transparent)'
                        : 'var(--edge)'
                    }`,
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 10,
                      padding: '13px 15px',
                    }}
                  >
                    <span style={{ minWidth: 0 }}>
                      <span
                        style={{
                          display: 'block',
                          fontFamily: 'var(--font-display)',
                          fontSize: 15,
                          fontWeight: 700,
                          letterSpacing: '-0.01em',
                          color: 'var(--ink-1)',
                        }}
                      >
                        {MEAL_LABEL[meal]}
                      </span>
                      <span
                        style={{
                          display: 'block',
                          fontFamily: 'var(--font-mono)',
                          fontSize: 11,
                          marginTop: 3,
                          fontVariantNumeric: 'tabular-nums',
                          color: isOver ? 'var(--off-track)' : 'var(--ink-3)',
                        }}
                      >
                        {budget != null
                          ? `${kcal} / ${budget} kcal${isOver ? ` · over by ${kcal - budget}` : ''}`
                          : `${kcal} kcal logged`}
                      </span>
                    </span>
                    <button
                      type="button"
                      onClick={() => openBrowse(meal)}
                      aria-label={`Add food to ${MEAL_LABEL[meal]}`}
                      data-testid={`food-log-add-${meal}`}
                      style={{
                        width: 40,
                        height: 40,
                        flexShrink: 0,
                        display: 'grid',
                        placeItems: 'center',
                        borderRadius: 'var(--r-sm)',
                        cursor: 'pointer',
                        border: `1px solid ${empty ? 'transparent' : 'var(--edge)'}`,
                        background: empty
                          ? 'linear-gradient(180deg, var(--ember-lit), var(--ember))'
                          : 'var(--paper-3)',
                        color: empty ? 'var(--ember-ink)' : 'var(--ink-2)',
                        boxShadow: empty ? 'var(--ring-ember)' : 'var(--elev-1)',
                      }}
                    >
                      <Icon name="plus" size={18} />
                    </button>
                  </div>

                  {mealEntries.length > 0 && (
                    <div style={{ borderTop: '1px solid var(--edge)', padding: '4px 0 8px' }}>
                      {mealEntries.map((entry) => {
                        const food = foodMap.get(entry.foodId);
                        return (
                          <div
                            key={entry.id}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: 10,
                              padding: '7px 15px',
                            }}
                          >
                            <button
                              type="button"
                              onClick={() => startEditExisting(entry)}
                              data-testid={`food-log-history-row-${entry.id}`}
                              style={{
                                flex: 1,
                                minWidth: 0,
                                textAlign: 'left',
                                background: 'none',
                                border: 0,
                                padding: 0,
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'baseline',
                                justifyContent: 'space-between',
                                gap: 10,
                              }}
                            >
                              <span
                                style={{
                                  fontSize: 13,
                                  color: 'var(--ink-2)',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {formatNutritionLog(entry, food)}
                              </span>
                              <span
                                style={{
                                  fontFamily: 'var(--font-mono)',
                                  fontSize: 12,
                                  color: 'var(--ink-3)',
                                  fontVariantNumeric: 'tabular-nums',
                                  flexShrink: 0,
                                }}
                              >
                                {Math.round(entry.kcal)} kcal
                              </span>
                            </button>
                            <button
                              type="button"
                              onClick={() => void deleteEntry(entry)}
                              aria-label={`Delete ${food?.name ?? entry.foodId}`}
                              style={{
                                flexShrink: 0,
                                display: 'grid',
                                placeItems: 'center',
                                width: 26,
                                height: 26,
                                borderRadius: 'var(--r-full)',
                                background: 'transparent',
                                border: '1px solid var(--edge)',
                                color: 'var(--ink-3)',
                                cursor: 'pointer',
                              }}
                            >
                              <Icon name="close" size={13} />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      ) : null}

      <div style={{ ...EYEBROW, margin: '22px 0 10px' }}>Last 7 days</div>
      <div style={{ ...CARD, padding: 18 }}>
        {days.length > 0 ? (
          <ConsistencyStrip days={days} height={56} />
        ) : (
          <SkeletonBlock width="100%" height={56} />
        )}
        <p
          style={{
            margin: '14px 0 0',
            fontSize: 'var(--text-body-sm-size)',
            lineHeight: 'var(--text-body-sm-line)',
            color: 'var(--ink-2)',
          }}
        >
          {consistencySentence(days)}
        </p>
      </div>

      <FlowSheet
        open={sheet.kind !== 'closed'}
        onClose={closeSheet}
        // Browse mode: pin the sheet to a fixed height so it does NOT shrink as
        // the result count changes. Otherwise the bottom-anchored sheet contracts
        // when a search narrows to 1-2 foods, dragging the search input downward
        // mid-type. Fixed height keeps the input anchored and scrolls results.
        style={sheet.kind === 'browse' ? { height: '86%' } : undefined}
        title={
          sheet.kind === 'new'
            ? sheet.food.name
            : sheet.kind === 'edit'
              ? (activeFood?.name ?? 'Edit entry')
              : sheet.kind === 'browse'
                ? `Add to ${MEAL_LABEL[sheet.meal]}`
                : ''
        }
        meta={
          sheet.kind === 'browse'
            ? kcalTarget > 0
              ? slotLeft >= 0
                ? `${slotLeft} kcal left in this slot`
                : `${Math.abs(slotLeft)} kcal over in this slot`
              : 'Search the catalog or pick a recent food'
            : activeFood
              ? `${activeFood.kcalPer100g} kcal · ${round1(activeFood.proteinPer100g)} g P / 100 g`
              : undefined
        }
        footer={
          sheet.kind === 'closed' ? null : (
            <>
              <FlowButton variant="quiet" block onClick={sheetBack}>
                {sheet.kind === 'new' ? 'Back' : 'Cancel'}
              </FlowButton>
              {sheet.kind !== 'browse' && (
                <FlowButton
                  variant="primary"
                  block
                  disabled={isPending}
                  onClick={() => void save()}
                  data-testid="food-log-save"
                >
                  {isPending
                    ? 'Saving…'
                    : sheet.kind === 'new'
                      ? `Add ${preview?.kcal ?? 0} kcal`
                      : 'Save'}
                </FlowButton>
              )}
            </>
          )
        }
      >
        {sheet.kind === 'browse' && (
          <>
            <FlowInput
              placeholder="Search foods…"
              aria-label="Search foods"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 2 }}>
              {CATEGORY_TABS.map((t) => {
                const on = category === t.key;
                return (
                  <button
                    key={t.label}
                    type="button"
                    role="tab"
                    aria-selected={on}
                    onClick={() => setCategory(t.key)}
                    style={{
                      flexShrink: 0,
                      padding: '8px 14px',
                      borderRadius: 'var(--r-full)',
                      cursor: 'pointer',
                      fontFamily: 'var(--font-ui)',
                      fontSize: 12,
                      fontWeight: 600,
                      border: `1px solid ${
                        on ? 'color-mix(in srgb, var(--ember) 45%, transparent)' : 'var(--edge)'
                      }`,
                      background: on ? 'var(--ember-wash)' : 'var(--paper-3)',
                      color: on ? 'var(--ember)' : 'var(--ink-2)',
                    }}
                  >
                    {t.label}
                  </button>
                );
              })}
            </div>

            {!search.trim() && category === null && favoriteFoods.data?.items.length ? (
              <FoodGroup
                title="Favorites"
                rows={favoriteFoods.data.items.map(({ food }) => ({ food, meta: '★' }))}
                onPick={startEditNew}
              />
            ) : null}
            {!search.trim() && category === null && recentFoods.data?.items.length ? (
              <FoodGroup
                title="Recent"
                rows={recentFoods.data.items
                  .slice(0, 6)
                  .map(({ food, timesLogged }) => ({ food, meta: `${timesLogged}×` }))}
                onPick={startEditNew}
              />
            ) : null}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingBottom: 14 }}>
              {foods.isLoading && !foods.data ? (
                [0, 1, 2, 3, 4].map((i) => <SkeletonBlock key={i} width="100%" height={56} />)
              ) : filteredFoods.length === 0 ? (
                <p style={{ fontSize: 13, color: 'var(--ink-3)' }}>
                  {search.trim()
                    ? `No foods found for "${search.trim()}". We'll add more soon.`
                    : 'No foods in this category.'}
                </p>
              ) : (
                filteredFoods.map((food) => (
                  <FoodRow
                    key={food.id}
                    food={food}
                    meta={`${food.kcalPer100g} kcal · ${round1(food.proteinPer100g)} g P / 100 g`}
                    onPick={startEditNew}
                  />
                ))
              )}
            </div>
          </>
        )}

        {(sheet.kind === 'new' || sheet.kind === 'edit') && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, paddingBottom: 8 }}>
            {activeFood && sheet.kind === 'new' ? (
              <button
                type="button"
                onClick={() =>
                  toggleFavorite.mutate({
                    foodId: activeFood.id,
                    favorite: !favoriteIds.has(activeFood.id),
                  })
                }
                disabled={toggleFavorite.isPending}
                style={{
                  alignSelf: 'flex-start',
                  background: 'none',
                  border: 0,
                  padding: 0,
                  cursor: 'pointer',
                  color: 'var(--ember)',
                  fontFamily: 'var(--font-mono)',
                  fontSize: 10,
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                }}
              >
                {favoriteIds.has(activeFood.id) ? '★ Remove favorite' : '☆ Add favorite'}
              </button>
            ) : null}

            {inputMode === 'count' && (
              <>
                <div
                  data-testid="food-log-count-stepper"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    background: 'var(--paper-0)',
                    border: '1px solid var(--edge)',
                    borderRadius: 'var(--r-sm)',
                    boxShadow: 'inset 0 1px 3px rgba(0,0,0,.45)',
                    padding: '10px 14px',
                  }}
                >
                  <StepperButton
                    label="Decrease count"
                    disabled={count <= COUNT_MIN}
                    onClick={() => setCount((c) => clampCount(c - 1))}
                  >
                    −
                  </StepperButton>
                  <span
                    aria-live="polite"
                    style={{
                      fontFamily: 'var(--font-display)',
                      fontSize: 30,
                      fontWeight: 700,
                      letterSpacing: '-0.04em',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {count}
                  </span>
                  <StepperButton
                    label="Increase count"
                    disabled={count >= COUNT_MAX}
                    onClick={() => setCount((c) => clampCount(c + 1))}
                  >
                    +
                  </StepperButton>
                </div>
                {activeSizes && (
                  <SizeChips
                    testId="food-log-size-chips"
                    chipTestIdPrefix="food-log-size-chip"
                    ariaLabel="Size"
                    sizes={activeSizes}
                    value={sizeLabel}
                    onSelect={setSizeLabel}
                    unit={measureUnit}
                  />
                )}
                <p style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--ink-3)' }}>
                  {count} ×{' '}
                  {activeSizes && sizeLabel
                    ? `${sizeLabel} = ${fmtQuantity(count * activeSizes[sizeLabel], measureUnit)}`
                    : `default = ${fmtQuantity(grams, measureUnit)}`}
                </p>
              </>
            )}

            {inputMode === 'portion' && activeSizes && (
              <>
                <SizeChips
                  testId="food-log-portion-chips"
                  chipTestIdPrefix="food-log-portion-chip"
                  ariaLabel="Portion"
                  sizes={activeSizes}
                  value={portionLabel}
                  onSelect={setPortionLabel}
                  unit={measureUnit}
                />
                <p style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--ink-3)' }}>
                  {portionLabel} → {fmtQuantity(activeSizes[portionLabel], measureUnit)}
                </p>
              </>
            )}

            {/* Named household measures (handful, tablespoon, small bowl …) for
                grams-mode foods that carry them. A pick fills quantityG; the ×N
                stepper multiplies it. Resolves to grams, so the log payload is
                unchanged. "Enter grams" drops to the free-weight slider. */}
            {inputMode === 'grams' && activeMeasures && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div
                  role="group"
                  aria-label="Measurement unit"
                  style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}
                >
                  {activeMeasures.map((m) => {
                    const on = measureLabel === m.label;
                    return (
                      <button
                        key={m.label}
                        type="button"
                        data-testid={`food-log-measure-${m.label.replace(/\s+/g, '-')}`}
                        aria-pressed={on}
                        onClick={() => applyMeasure(m)}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'baseline',
                          gap: 6,
                          padding: '8px 12px',
                          borderRadius: 'var(--r-sm)',
                          border: on ? '1px solid var(--ember)' : '1px solid var(--edge)',
                          background: on
                            ? 'var(--ember-dim, rgba(220,110,60,.14))'
                            : 'var(--paper-0)',
                          color: 'var(--ink-1)',
                          cursor: 'pointer',
                          fontSize: 14,
                          fontWeight: on ? 600 : 500,
                          textTransform: 'capitalize',
                        }}
                      >
                        {m.label}
                        <span
                          style={{
                            fontFamily: 'var(--font-mono)',
                            fontSize: 11,
                            color: 'var(--ink-3)',
                            fontVariantNumeric: 'tabular-nums',
                          }}
                        >
                          {fmtQuantity(m.grams, measureUnit)}
                        </span>
                      </button>
                    );
                  })}
                </div>
                {measureLabel &&
                  (() => {
                    const m = activeMeasures.find((x) => x.label === measureLabel);
                    if (!m) return null;
                    return (
                      <div
                        style={{
                          background: 'var(--paper-0)',
                          border: '1px solid var(--edge)',
                          borderRadius: 'var(--r-sm)',
                          boxShadow: 'inset 0 1px 3px rgba(0,0,0,.45)',
                          padding: '14px 16px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 12,
                        }}
                      >
                        <div
                          role="group"
                          aria-label="Quantity"
                          style={{ display: 'flex', alignItems: 'center', gap: 12 }}
                        >
                          <button
                            type="button"
                            data-testid="food-log-measure-dec"
                            aria-label="Decrease quantity"
                            onClick={() => stepMeasureCount(m, -1)}
                            disabled={measureCount <= COUNT_MIN}
                            style={{
                              width: 34,
                              height: 34,
                              borderRadius: 'var(--r-sm)',
                              border: '1px solid var(--edge)',
                              background: 'var(--paper-1)',
                              color: 'var(--ink-1)',
                              fontSize: 20,
                              cursor: measureCount <= COUNT_MIN ? 'not-allowed' : 'pointer',
                              opacity: measureCount <= COUNT_MIN ? 0.4 : 1,
                            }}
                          >
                            −
                          </button>
                          <span
                            style={{
                              fontFamily: 'var(--font-display)',
                              fontSize: 26,
                              fontWeight: 700,
                              fontVariantNumeric: 'tabular-nums',
                              minWidth: 24,
                              textAlign: 'center',
                            }}
                          >
                            {measureCount}
                          </span>
                          <button
                            type="button"
                            data-testid="food-log-measure-inc"
                            aria-label="Increase quantity"
                            onClick={() => stepMeasureCount(m, 1)}
                            disabled={measureCount >= COUNT_MAX}
                            style={{
                              width: 34,
                              height: 34,
                              borderRadius: 'var(--r-sm)',
                              border: '1px solid var(--edge)',
                              background: 'var(--paper-1)',
                              color: 'var(--ink-1)',
                              fontSize: 20,
                              cursor: measureCount >= COUNT_MAX ? 'not-allowed' : 'pointer',
                              opacity: measureCount >= COUNT_MAX ? 0.4 : 1,
                            }}
                          >
                            +
                          </button>
                        </div>
                        <span
                          style={{
                            fontFamily: 'var(--font-mono)',
                            fontSize: 13,
                            color: 'var(--ink-3)',
                            fontVariantNumeric: 'tabular-nums',
                            textTransform: 'capitalize',
                          }}
                        >
                          {measureCount} × {m.label} = {fmtQuantity(quantityG, measureUnit)}
                        </span>
                      </div>
                    );
                  })()}
                <div>
                  <LinkButton testId="food-log-custom-grams-link" onClick={useCustomGrams}>
                    {measureUnit === 'ml' ? 'Enter millilitres manually' : 'Enter grams manually'}
                  </LinkButton>
                </div>
              </div>
            )}

            {((inputMode === 'grams' && measureLabel === null) || inputMode === 'manual') && (
              <div
                style={{
                  background: 'var(--paper-0)',
                  border: '1px solid var(--edge)',
                  borderRadius: 'var(--r-sm)',
                  boxShadow: 'inset 0 1px 3px rgba(0,0,0,.45)',
                  padding: '14px 16px',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    justifyContent: 'space-between',
                    gap: 10,
                    marginBottom: 10,
                  }}
                >
                  <span
                    style={{
                      fontFamily: 'var(--font-display)',
                      fontSize: 34,
                      fontWeight: 700,
                      letterSpacing: '-0.04em',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {quantityG}
                  </span>
                  <span
                    style={{ fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--ink-3)' }}
                  >
                    {measureUnit === 'ml' ? 'millilitres' : 'grams'}
                  </span>
                </div>
                <input
                  type="range"
                  min={QTY_MIN}
                  max={inputMode === 'manual' ? MANUAL_QTY_MAX : QTY_MAX}
                  step={QTY_STEP}
                  value={quantityG}
                  onChange={(e) =>
                    setQuantityG(
                      clampQty(
                        Number(e.target.value),
                        inputMode === 'manual' ? MANUAL_QTY_MAX : QTY_MAX,
                      ),
                    )
                  }
                  aria-label="Quantity in grams"
                  style={{ width: '100%', accentColor: 'var(--ember)' }}
                />
              </div>
            )}

            {overrideAvailable && inputMode !== 'manual' && (
              <LinkButton testId="food-log-override-link" onClick={() => setInputMode('manual')}>
                Log by weight instead
              </LinkButton>
            )}
            {overrideAvailable && inputMode === 'manual' && naturalMode && (
              <LinkButton
                testId="food-log-back-to-natural-link"
                onClick={() => setInputMode(naturalMode)}
              >
                Back to {naturalMode} input
              </LinkButton>
            )}

            <div role="group" aria-label="Meal" style={{ display: 'flex', gap: 6 }}>
              {MEAL_TYPES.map((m) => {
                const on = mealType === m;
                return (
                  <button
                    key={m}
                    type="button"
                    aria-pressed={on}
                    onClick={() => setMealType(m)}
                    style={{
                      flex: 1,
                      padding: '10px 0',
                      borderRadius: 'var(--r-full)',
                      cursor: 'pointer',
                      fontFamily: 'var(--font-ui)',
                      fontSize: 12,
                      fontWeight: 600,
                      border: `1px solid ${
                        on ? 'color-mix(in srgb, var(--ember) 45%, transparent)' : 'var(--edge)'
                      }`,
                      background: on ? 'var(--ember-wash)' : 'var(--paper-3)',
                      color: on ? 'var(--ember)' : 'var(--ink-2)',
                    }}
                  >
                    {MEAL_LABEL[m]}
                  </button>
                );
              })}
            </div>

            {preview && (
              <div
                style={{
                  background: 'var(--paper-2)',
                  border: '1px solid var(--edge)',
                  borderRadius: 'var(--r-md)',
                  padding: 14,
                  boxShadow: 'var(--elev-1)',
                }}
                aria-live="polite"
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    justifyContent: 'space-between',
                    gap: 10,
                  }}
                >
                  <span style={{ ...EYEBROW, fontSize: 11 }}>After adding</span>
                  <span
                    style={{
                      fontFamily: 'var(--font-mono)',
                      fontSize: 12,
                      fontVariantNumeric: 'tabular-nums',
                      color:
                        kcalTarget > 0 && total.kcal + preview.kcal > kcalTarget
                          ? 'var(--off-track)'
                          : 'var(--ink-2)',
                    }}
                  >
                    +{preview.kcal} kcal · day at{' '}
                    {Math.round(total.kcal + preview.kcal).toLocaleString()}
                  </span>
                </div>
                <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <MacroRow
                    channel="protein"
                    label="Protein"
                    current={round1(total.proteinG + preview.proteinG)}
                    target={target?.proteinMaxG ?? 0}
                  />
                  <MacroRow
                    channel="carbs"
                    label="Carbs"
                    current={round1(total.carbsG + preview.carbsG)}
                    target={target?.carbsMaxG ?? 0}
                  />
                  <MacroRow
                    channel="fat"
                    label="Fat"
                    current={round1(total.fatG + preview.fatG)}
                    target={target?.fatMaxG ?? 0}
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </FlowSheet>

      {pendingToast && (
        <div
          role="status"
          style={{
            position: 'fixed',
            left: 16,
            right: 16,
            bottom: 108,
            zIndex: 200,
            maxWidth: 560,
            margin: '0 auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            padding: '12px 14px',
            borderRadius: 'var(--r-sm)',
            background: 'color-mix(in srgb, var(--paper-3) 94%, transparent)',
            backdropFilter: 'blur(12px)',
            border: '1px solid var(--edge-strong)',
            boxShadow: 'var(--elev-3)',
            animation: 'v2-rise 260ms var(--ease-out) both',
          }}
        >
          <span style={{ minWidth: 0 }}>
            <span
              style={{
                display: 'block',
                fontSize: 13,
                fontWeight: 600,
                color: 'var(--ink-1)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {pendingToast.title}
            </span>
            <span
              style={{
                display: 'block',
                fontFamily: 'var(--font-mono)',
                fontSize: 11,
                color: 'var(--ink-3)',
                marginTop: 2,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {pendingToast.meta}
            </span>
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
            <button
              type="button"
              onClick={() => void undoLastAdd()}
              style={{
                background: 'none',
                border: 0,
                padding: 0,
                cursor: 'pointer',
                fontFamily: 'var(--font-mono)',
                fontSize: 10,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: 'var(--ember)',
              }}
            >
              Undo
            </button>
            <button
              type="button"
              onClick={() => setPendingToast(null)}
              aria-label="Dismiss"
              style={{
                width: 28,
                height: 28,
                display: 'grid',
                placeItems: 'center',
                borderRadius: 'var(--r-full)',
                background: 'var(--paper-2)',
                border: '1px solid var(--edge)',
                cursor: 'pointer',
                color: 'var(--ink-3)',
              }}
            >
              <Icon name="close" size={14} />
            </button>
          </span>
        </div>
      )}
    </section>
  );
}

function FoodGroup({
  title,
  rows,
  onPick,
}: {
  title: string;
  rows: ReadonlyArray<{ food: Food; meta: string }>;
  onPick: (food: Food) => void;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }} aria-label={title}>
      <span
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 10,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: 'var(--ink-3)',
        }}
      >
        {title}
      </span>
      {rows.map(({ food, meta }) => (
        <FoodRow key={food.id} food={food} meta={meta} onPick={onPick} />
      ))}
    </div>
  );
}

function FoodRow({
  food,
  meta,
  onPick,
}: {
  food: Food;
  meta: string;
  onPick: (food: Food) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onPick(food)}
      data-testid={`food-log-list-row-${food.id}`}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 10,
        width: '100%',
        textAlign: 'left',
        cursor: 'pointer',
        background: 'var(--paper-2)',
        border: '1px solid var(--edge)',
        borderRadius: 'var(--r-sm)',
        padding: '12px 14px',
        boxShadow: 'var(--elev-1)',
      }}
    >
      <span style={{ minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 14, fontWeight: 600, color: 'var(--ink-1)' }}>
          {food.name}
        </span>
        <span
          style={{
            display: 'block',
            fontFamily: 'var(--font-mono)',
            fontSize: 11,
            color: 'var(--ink-3)',
            marginTop: 3,
          }}
        >
          {meta}
        </span>
      </span>
      <span style={{ color: 'var(--ember)', flexShrink: 0, display: 'grid', placeItems: 'center' }}>
        <Icon name="plus" size={18} />
      </span>
    </button>
  );
}

function SizeChips({
  testId,
  chipTestIdPrefix,
  ariaLabel,
  sizes,
  value,
  onSelect,
  unit = 'g',
}: {
  testId: string;
  chipTestIdPrefix: string;
  ariaLabel: string;
  sizes: FoodSizes;
  value: FoodSizeLabel | null;
  onSelect: (label: FoodSizeLabel) => void;
  unit?: 'g' | 'ml';
}) {
  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      data-testid={testId}
      style={{ display: 'flex', gap: 6 }}
    >
      {SIZE_LABELS.map((label) => {
        const on = value === label;
        return (
          <button
            key={label}
            type="button"
            role="radio"
            aria-checked={on}
            onClick={() => onSelect(label)}
            data-testid={`${chipTestIdPrefix}-${label}`}
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              padding: '10px 0',
              borderRadius: 'var(--r-full)',
              cursor: 'pointer',
              fontFamily: 'var(--font-ui)',
              fontSize: 12,
              fontWeight: 600,
              border: `1px solid ${
                on ? 'color-mix(in srgb, var(--ember) 45%, transparent)' : 'var(--edge)'
              }`,
              background: on ? 'var(--ember-wash)' : 'var(--paper-3)',
              color: on ? 'var(--ember)' : 'var(--ink-2)',
            }}
          >
            <span>{label}</span>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--ink-3)' }}>
              {fmtQuantity(sizes[label], unit)}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function StepperButton({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled: boolean;
  onClick: () => void;
  children: string;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      style={{
        width: 40,
        height: 40,
        display: 'grid',
        placeItems: 'center',
        borderRadius: 'var(--r-full)',
        background: 'var(--paper-3)',
        border: '1px solid var(--edge)',
        color: disabled ? 'var(--ink-3)' : 'var(--ink-1)',
        fontSize: 20,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      {children}
    </button>
  );
}

function LinkButton({
  testId,
  onClick,
  children,
}: {
  testId: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      style={{
        alignSelf: 'flex-start',
        background: 'none',
        border: 0,
        padding: 0,
        cursor: 'pointer',
        color: 'var(--ember)',
        fontFamily: 'var(--font-mono)',
        fontSize: 10,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
      }}
    >
      {children}
    </button>
  );
}
